import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  Truck, TrendingUp, CalendarDays, BellRing, ChevronRight, Plus, X, Loader2,
  AlertTriangle, ShieldAlert, Info, Wallet, Receipt as ReceiptIcon,
  PackageCheck, PackageX, Target, Car, Trophy, Star, Lock,
  Gem, ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import LevelUpOverlay from "../components/LevelUpOverlay";
import { UpcomingPayments } from "../components/UpcomingPayments";
import { TodayTodo } from "../components/TodayTodo";
import { Workspace } from "../lib/api";
import { PageSkeleton } from "../components/PageSkeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pulpit Główny — 2×2 grid of live tiles.
 *   [ Live fleet map ]   [ Cashflow chart      ]
 *   [ Mini calendar   ]  [ Payment alerts      ]
 * Each tile is symmetric (h = 50vh calc after header), rounded, dark-themed,
 * and clickable where a natural drill-down exists.
 *
 * For users with role "kierowca" a dedicated driver dashboard is rendered
 * instead (vehicle card + delivery KPIs).
 */
export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === "kierowca") return <DriverDashboard />;
  return <ManagerDashboard />;
}

function ManagerDashboard() {
  const [data, setData] = useState(null);
  const [todo, setTodo] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    Promise.all([
      axios.get(`${API}/dashboard/summary?days_ahead=60`, { withCredentials: true }),
      axios.get(`${API}/fleet`, { withCredentials: true }).catch(() => ({ data: [] })),
      Workspace.todo().catch(() => null),
    ])
      .then(([s, f, t]) => {
        setData(s.data);
        setFleet(Array.isArray(f.data) ? f.data : []);
        setTodo(t);
      })
      .catch(() => toast.error("Nie mogę pobrać danych pulpitu"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    const int = setInterval(load, 60000);
    return () => clearInterval(int);
  }, []);

  if (loading) {
    return (
      <PageSkeleton variant="grid" label="Ładuję pulpit…" />
    );
  }

  return (
    <div className="p-3 md:p-4" data-testid="dashboard-page">
      <TodayTodo data={todo} />
      <UpcomingPayments data={data?.payment_reminders} onReload={load} />
      <div className="stagger grid grid-cols-1 md:grid-cols-2 md:grid-rows-2 gap-3 md:gap-4 md:h-[calc(100vh-108px)]">
        <TileMap fleet={fleet} />
        <TileCashflow data={data?.cashflow || []} />
        <TileCalendar events={data?.events || []} onReload={load} />
        <TileAlerts alerts={data?.alerts || []} />
      </div>
    </div>
  );
}

// ==========================================================================
// TILE 1 — Live fleet map
// ==========================================================================

/** Ensures Leaflet recalculates its size once the parent grid finishes
 *  laying out. Without this the map often renders with 0×0 or partial
 *  tiles when placed inside a CSS-grid cell.                              */
function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const invalidate = () => map.invalidateSize();
    // Immediately after mount + once more after layout has settled
    invalidate();
    const t1 = setTimeout(invalidate, 150);
    const t2 = setTimeout(invalidate, 500);
    // React to window resizes so the tile always fills its cell
    window.addEventListener("resize", invalidate);
    // Observe the actual map container so grid re-flows also trigger it
    let ro;
    const el = map.getContainer();
    if (typeof ResizeObserver !== "undefined" && el) {
      ro = new ResizeObserver(() => map.invalidateSize());
      ro.observe(el);
    }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", invalidate);
      if (ro) ro.disconnect();
    };
  }, [map]);
  return null;
}

function TileMap({ fleet }) {
  const nav = useNavigate();
  // Fallback positions clustered around Szczecin when vehicles have no GPS.
  // If there's no fleet yet, sprinkle 6 demo pins around the city so the
  // tile actually shows something "live" instead of an empty dark canvas.
  const positions = useMemo(() => {
    const center = [53.4285, 14.5528];
    const source = fleet && fleet.length > 0
      ? fleet
      : Array.from({ length: 6 }, (_, i) => ({
          id: `demo-${i}`,
          registration: `ZS ${1000 + i * 111}A`,
          brand: "GoPossible",
          status: "active",
          _demo: true,
        }));
    return source.map((v, i) => {
      const lat = v.gps_lat ?? center[0] + Math.sin(i * 1.7 + 0.3) * (0.03 + (i % 3) * 0.015);
      const lng = v.gps_lng ?? center[1] + Math.cos(i * 2.1 + 0.7) * (0.05 + (i % 3) * 0.02);
      return {
        id: v.id,
        lat, lng,
        label: v.registration || v.brand || `Pojazd ${i + 1}`,
        status: v.status || "active",
        demo: v._demo,
      };
    });
  }, [fleet]);

  // Pulse "tick" — bumps every 3s to animate marker radius (feels alive).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 1000), 1500);
    return () => clearInterval(id);
  }, []);

  return (
    <TileFrame
      onClick={() => nav("/fleet")}
      testId="tile-fleet-map"
      title="Mapa GPS · Podgląd na żywo"
      subtitle="Pozycje pojazdów"
      icon={Truck}
      accent="from-red-500/20 to-transparent"
      minHeightCls="min-h-[300px]"
    >
      <div className="relative w-full h-full min-h-[240px]">
        <MapContainer
          center={[53.4285, 14.5528]}
          zoom={12}
          scrollWheelZoom={false}
          dragging={false}
          zoomControl={false}
          doubleClickZoom={false}
          touchZoom={false}
          style={{ height: "100%", width: "100%", background: "#18181b" }}
          className="rounded-b-2xl"
        >
          <MapResizer />
          <TileLayer
            attribution=""
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          {positions.map((p, i) => {
            const pulse = 6 + ((tick + i) % 3) * 1.5;
            return (
              <CircleMarker
                key={p.id}
                center={[p.lat, p.lng]}
                radius={pulse}
                pathOptions={{
                  color: "#ffffff",
                  weight: 2,
                  fillColor: p.demo ? "#f59e0b" : "#E52C34",
                  fillOpacity: 0.9,
                }}
              >
                <Popup>
                  <b>{p.label}</b>
                  <br />
                  <span className="text-xs">{p.status}{p.demo ? " · demo" : ""}</span>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
        {/* LIVE badge — pulsing dot + text, sits above the map */}
        <div className="absolute top-2 left-2 z-[400] pointer-events-none flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-md ring-1 ring-white/20">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
          </span>
          <span className="text-[9px] font-mono uppercase tracking-widest text-white/80 font-bold">Live</span>
        </div>
        <div className="absolute bottom-2 right-3 text-[10px] font-mono text-white/70 uppercase tracking-widest z-[400] pointer-events-none bg-black/40 backdrop-blur-md px-2 py-1 rounded">
          {positions.length} pojazdów · Kliknij po szczegóły →
        </div>
      </div>
    </TileFrame>
  );
}

// ==========================================================================
// TILE 2 — Cashflow chart
// ==========================================================================

function TileCashflow({ data }) {
  const nav = useNavigate();
  return (
    <TileFrame
      onClick={() => nav("/finance")}
      testId="tile-cashflow"
      title="Cashflow · Ostatnie 6 miesięcy"
      subtitle="Przychody vs koszty (realne)"
      icon={TrendingUp}
      accent="from-blue-500/20 to-transparent"
    >
      <div className="w-full h-full min-h-[220px] p-3">
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={200} initialDimension={{ width: 300, height: 220 }}>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" vertical={false} />
            <XAxis dataKey="label" stroke="#a1a1aa" style={{ fontSize: 11 }} />
            <YAxis stroke="#a1a1aa" style={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", color: "#fafafa" }}
              formatter={(v) => `${Number(v).toLocaleString("pl-PL", { minimumFractionDigits: 2 })} PLN`}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
            <Bar dataKey="income" name="Przychód" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expense" name="Koszty" fill="#ef4444" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </TileFrame>
  );
}

// ==========================================================================
// TILE 3 — Calendar
// ==========================================================================

function TileCalendar({ events, onReload }) {
  const [showAdd, setShowAdd] = useState(false);
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOfGrid = new Date(firstDay);
  const dow = (firstDay.getDay() + 6) % 7; // Monday=0
  startOfGrid.setDate(firstDay.getDate() - dow);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(startOfGrid);
    d.setDate(startOfGrid.getDate() + i);
    cells.push(d);
  }
  const isCurrentMonth = (d) => d.getMonth() === month;
  const isToday = (d) => d.toDateString() === now.toDateString();
  const eventsFor = (d) => {
    const key = d.toISOString().slice(0, 10);
    return events.filter((e) => e.date === key);
  };
  const monthLabel = firstDay.toLocaleDateString("pl-PL", { month: "long", year: "numeric" });

  return (
    <TileFrame
      testId="tile-calendar"
      title={`Kalendarz · ${monthLabel}`}
      subtitle={`${events.length} wydarzeń w horyzoncie`}
      icon={CalendarDays}
      accent="from-emerald-500/20 to-transparent"
      action={
        <button
          onClick={(e) => { e.stopPropagation(); setShowAdd(true); }}
          data-testid="calendar-add-event"
          className="text-xs font-bold uppercase tracking-wider bg-white/10 hover:bg-white/25 text-white px-2.5 py-1 backdrop-blur-md rounded"
        >
          <Plus className="inline w-3 h-3 mr-1" /> Wydarzenie
        </button>
      }
    >
      <div className="p-3 h-full flex flex-col">
        <div className="grid grid-cols-7 gap-1 text-[9px] font-mono uppercase text-white/40 mb-1">
          {["Pn", "Wt", "Śr", "Cz", "Pt", "Sb", "Nd"].map((d) => (
            <div key={d} className="text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 flex-1">
          {cells.map((d, i) => {
            const dayEvents = eventsFor(d);
            return (
              <div
                key={i}
                className={`relative rounded flex flex-col items-center justify-center text-[11px] transition-colors ${
                  isToday(d)
                    ? "bg-brand text-white font-bold"
                    : isCurrentMonth(d)
                    ? "bg-white/5 text-white/80 hover:bg-white/10"
                    : "text-white/25"
                }`}
              >
                {d.getDate()}
                {dayEvents.length > 0 && (
                  <div className="absolute bottom-0.5 flex gap-0.5">
                    {dayEvents.slice(0, 3).map((_, k) => (
                      <span key={k} className="w-1 h-1 rounded-full bg-emerald-400" />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {showAdd && <AddEventModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); onReload(); }} />}
    </TileFrame>
  );
}

function AddEventModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "",
    date: new Date().toISOString().slice(0, 10),
    start_time: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title.trim()) return toast.error("Podaj tytuł wydarzenia");
    setSaving(true);
    try {
      await axios.post(`${API}/dashboard/events`, form, { withCredentials: true });
      toast.success("Wydarzenie dodane");
      onSaved();
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[1400] bg-zinc-950/70 backdrop-blur-xl flex items-center justify-center p-4"
         onClick={onClose} data-testid="add-event-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full max-w-md rounded-2xl p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-black uppercase tracking-tight text-lg">Nowe wydarzenie</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <input placeholder="Tytuł" value={form.title}
                 data-testid="event-title"
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 className="w-full border-2 border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   className="w-full border-2 border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
            <input type="time" value={form.start_time}
                   onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                   className="w-full border-2 border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
          </div>
          <textarea placeholder="Notatka (opcjonalnie)" value={form.note} rows={2}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className="w-full border-2 border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900 resize-none" />
          <button onClick={save} disabled={saving}
                  data-testid="event-save-btn"
                  className="w-full bg-zinc-900 text-white font-bold uppercase tracking-wider py-2.5 hover:bg-zinc-800 disabled:opacity-50">
            {saving ? "Zapisuję…" : "Zapisz wydarzenie"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// TILE 4 — Payment alerts
// ==========================================================================

function TileAlerts({ alerts }) {
  const iconFor = (kind) => ({
    zus: ShieldAlert, pit: ShieldAlert, vat: ShieldAlert,
    salary: Wallet, invoice_income: ReceiptIcon, invoice_expense: ReceiptIcon,
  }[kind] || Info);

  const badgeCls = (severity) => ({
    urgent: "bg-red-500/25 text-red-100 border-red-500/50",
    warning: "bg-amber-500/25 text-amber-100 border-amber-500/50",
    info: "bg-white/10 text-white/70 border-white/20",
  }[severity]);

  return (
    <TileFrame
      testId="tile-alerts"
      title="Nadchodzące terminy"
      subtitle={`${alerts.length} zdarzeń · 60 dni naprzód`}
      icon={BellRing}
      accent="from-amber-500/20 to-transparent"
    >
      <div className="overflow-y-auto flex-1 divide-y divide-white/10">
        {alerts.length === 0 && (
          <div className="p-6 text-center text-white/40 text-sm">
            Brak zbliżających się terminów w ciągu 60 dni.
          </div>
        )}
        {alerts.slice(0, 30).map((a, i) => {
          const Icon = iconFor(a.kind);
          return (
            <div
              key={`${a.kind}-${a.date}-${i}`}
              data-testid={`alert-${a.kind}-${a.date}`}
              className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors"
            >
              <Icon className={`w-4 h-4 flex-shrink-0 ${
                a.severity === "urgent" ? "text-red-400" :
                a.severity === "warning" ? "text-amber-400" : "text-white/50"
              }`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white truncate">{a.title}</div>
                <div className="text-[10px] font-mono text-white/50 truncate">{a.description}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[10px] font-mono text-white/60">{a.date}</div>
                <div className={`inline-block mt-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase border rounded ${badgeCls(a.severity)}`}>
                  {a.days_to <= 0 ? "dziś" : `za ${a.days_to}d`}
                </div>
              </div>
              {a.amount != null && (
                <div className="text-right text-xs font-mono font-bold text-white/80 ml-1 w-20">
                  {a.amount.toLocaleString("pl-PL", { minimumFractionDigits: 0 })} zł
                </div>
              )}
            </div>
          );
        })}
      </div>
    </TileFrame>
  );
}

// ==========================================================================
// Reusable Tile wrapper — uniform look for the 2×2 grid
// ==========================================================================

function TileFrame({ children, onClick, testId, title, subtitle, icon: Icon, accent, action, minHeightCls }) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      data-testid={testId}
      className={`relative min-w-0 overflow-hidden rounded-2xl bg-zinc-950 text-white shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)] ring-1 ring-white/10 flex flex-col ${minHeightCls || "min-h-[280px]"} ${
        clickable ? "cursor-pointer transition-transform hover:-translate-y-0.5 hover:ring-white/25" : ""
      }`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${accent || ""} pointer-events-none`} />
      <div className="relative flex items-center gap-3 px-4 py-3 border-b border-white/10 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 truncate">
            // {title}
          </div>
          <div className="text-sm font-bold truncate">{subtitle}</div>
        </div>
        {action}
        {clickable && <ChevronRight className="w-4 h-4 text-white/40" />}
      </div>
      <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
        {children}
      </div>
    </div>
  );
}


// ==========================================================================
// DRIVER DASHBOARD — shown when user.role === "kierowca"
// ==========================================================================

function DriverDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [levelUp, setLevelUp] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/dashboard/driver-stats`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setStats(r.data);
        // Fire one-shot level-up animation the FIRST time backend reports a
        // fresh tier crossing (backend clears its flag right after emission).
        const lu = r.data?.rpg?.level_up;
        if (lu) setLevelUp(lu);
      })
      .catch(() => toast.error("Nie mogę pobrać statystyk kierowcy"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <PageSkeleton label="Ładuję pulpit kierowcy…" />
    );
  }

  const v = stats?.vehicle;
  const m = stats?.month || {};
  const today = stats?.today;
  const eff = m.effectiveness == null ? null : Math.round(m.effectiveness * 1000) / 10;
  const now = new Date().toLocaleDateString("pl-PL", { month: "long", year: "numeric" });
  const rpg = stats?.rpg;
  const wallet = stats?.wallet;

  return (
    <div className="p-3 md:p-6 space-y-4" data-testid="driver-dashboard">
      {levelUp && (
        <LevelUpOverlay
          levelUp={levelUp}
          onClose={() => {
            setLevelUp(null);
            // Persist acknowledgement so React StrictMode / refresh doesn't replay.
            axios.post(`${API}/dashboard/level-up/ack`, {}, { withCredentials: true })
              .catch(() => {});
          }}
        />
      )}
      {/* ═════════════════════════════════════════════════════════════════
          JRPG CHARACTER CARD — driver's main hero (level, XP, boost)
      ══════════════════════════════════════════════════════════════════ */}
      <DriverHeroCard user={user} rpg={rpg} wallet={wallet} />

      {/* Greeting (compact now that the hero card is on top) */}
      <div className="flex items-baseline gap-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          // Pulpit Kierowcy · {now}
        </div>
      </div>

      {/* TRASA NA DZIŚ — big hero widget */}
      <div
        data-testid="driver-today-route"
        onClick={() => today && nav(`/moja-trasa`)}
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand via-red-700 to-zinc-950 text-white p-5 md:p-6 ring-1 ring-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)] ${today ? "cursor-pointer hover:ring-white/25 transition-all" : ""}`}
      >
        <Truck className="absolute -right-6 -bottom-10 w-56 h-56 opacity-[0.10]" strokeWidth={1.5} />
        <div className="relative flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center ring-1 ring-white/25">
            <Truck className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
              // Trasa na dziś
            </div>
            {today?.is_today === false && (
              <div className="text-[10px] font-mono text-amber-200">
                (nie masz nowej trasy na dziś — pokazuję ostatnią)
              </div>
            )}
          </div>
          {today && (
            <ChevronRight className="w-5 h-5 ml-auto text-white/60" />
          )}
        </div>

        {today ? (
          <div className="relative mt-4 grid md:grid-cols-3 gap-4 md:gap-6">
            <div className="md:col-span-2">
              <div className="text-2xl md:text-3xl font-heading font-black tracking-tight uppercase truncate">
                {today.name}
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-white/80 flex-wrap">
                {today.vehicle_plate && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/10 ring-1 ring-white/20">
                    <Car className="w-3 h-3" /> {today.vehicle_plate}
                  </span>
                )}
                {today.assigned_at && (
                  <span className="font-mono text-white/60">
                    {new Date(today.assigned_at).toLocaleString("pl-PL")}
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <MiniStat label="Doręczone" value={today.delivered} tone="emerald" />
              <MiniStat label="Do zrobienia" value={today.pending} tone="amber" />
              <MiniStat label="Nie dor." value={today.failed} tone="red" />
            </div>
          </div>
        ) : (
          <div className="relative mt-6 text-white/80 text-sm">
            Nie masz żadnej trasy przypisanej. Poproś koordynatora o wysłanie manifestu do Ciebie.
          </div>
        )}
      </div>

      {/* Wallet — hero widget */}
      <div
        data-testid="driver-wallet"
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-700 to-zinc-950 text-white p-5 md:p-6 ring-1 ring-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)]"
      >
        <Wallet className="absolute -right-6 -bottom-10 w-56 h-56 opacity-[0.10]" strokeWidth={1.5} />
        <div className="relative flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center ring-1 ring-white/25">
            <Wallet className="w-4 h-4" />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
            // Portfel · do wypłaty
          </div>
        </div>
        <div className="relative mt-3 flex items-end gap-8 flex-wrap">
          <div>
            <div className="text-4xl md:text-5xl font-heading font-black tracking-tight">
              {(stats?.wallet?.available_balance ?? 0).toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
            </div>
            <div className="text-xs mt-1 text-white/80">
              {stats?.wallet?.approved_total ?? 0} paczek · stawka{" "}
              {stats?.wallet?.rate_per_package ?? 0} zł/paczka
              {(stats?.wallet?.multipack_extra_total ?? 0) > 0 && (
                <>
                  <br />
                  <span className="text-amber-200" data-testid="wallet-multipack-hint">
                    + {stats.wallet.multipack_extra_total} multipack
                    {" · "}
                    {stats?.wallet?.multipack_extra_rate ?? 0.5} zł/szt
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="text-[11px] font-mono text-white/70 leading-relaxed">
            W tym miesiącu:{" "}
            <span className="text-emerald-200 font-bold text-lg">
              {(stats?.wallet?.monthly_earnings ?? 0).toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
            </span>
            <br />
            {stats?.wallet?.approved_month ?? 0} paczek zatwierdzonych
          </div>
        </div>
      </div>

      {/* RPG — level + achievements */}
      <RpgWidget rpg={stats?.rpg} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        {/* Vehicle card */}
        <div
          data-testid="driver-vehicle-card"
          onClick={() => v && nav("/fleet")}
          className={`md:col-span-1 relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-600 via-red-700 to-zinc-950 text-white p-5 min-h-[220px] ring-1 ring-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)] ${v ? "cursor-pointer" : ""}`}
        >
          <Car className="absolute -right-4 -bottom-6 w-40 h-40 opacity-[0.10]" strokeWidth={1.5} />
          <div className="relative flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
              <Car className="w-4 h-4" />
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
              // Twój pojazd
            </div>
          </div>
          {v ? (
            <div className="relative mt-4">
              <div className="text-3xl font-heading font-black tracking-tight uppercase">
                {v.plate || "—"}
              </div>
              <div className="text-sm mt-1 text-white/85">
                {[v.make, v.model, v.year].filter(Boolean).join(" · ") || "Bez opisu"}
              </div>
              {v.vin && (
                <div className="text-[10px] font-mono text-white/50 mt-3">VIN: {v.vin}</div>
              )}
            </div>
          ) : (
            <div className="relative mt-6 text-white/80 text-sm">
              Brak przypisanego pojazdu.
              <br />
              <span className="text-[11px] text-white/60">
                Skontaktuj się z koordynatorem żeby dostać auto.
              </span>
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          <KpiCard
            testId="driver-kpi-delivered"
            icon={PackageCheck}
            label="Doręczone w mies."
            value={m.delivered ?? 0}
            hint={`Suma z ${m.assignments_count ?? 0} tras`}
            tone="emerald"
          />
          <KpiCard
            testId="driver-kpi-failed"
            icon={PackageX}
            label="Nie doręczone w mies."
            value={m.failed ?? 0}
            hint={`w ${now}`}
            tone="red"
          />
          <KpiCard
            testId="driver-kpi-effectiveness"
            icon={Target}
            label="Skuteczność"
            value={eff == null ? "—" : `${eff}%`}
            hint={`${m.total_finished ?? 0} zakończonych`}
            tone="sky"
            big
          />
          <KpiCard
            testId="driver-kpi-pending"
            icon={CalendarDays}
            label="W trakcie (dziś)"
            value={today?.pending ?? 0}
            hint="paczek do końca pracy"
            tone="amber"
          />
          <KpiCard
            testId="driver-kpi-assignments"
            icon={Truck}
            label="Trasy w tym mies."
            value={m.assignments_count ?? 0}
            hint="dziennych przypisań"
            tone="zinc"
          />
          <KpiCard
            testId="driver-kpi-cod"
            icon={Wallet}
            label="Pobrania (trasa)"
            value={(today?.cod_total ?? 0).toLocaleString("pl-PL", { minimumFractionDigits: 2 })}
            hint={`zł · zebrane: ${(today?.cod_collected ?? 0).toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł`}
            tone="indigo"
          />
        </div>
      </div>

      {/* Recent routes */}
      <div className="bg-zinc-950 text-white rounded-2xl overflow-hidden ring-1 ring-white/10">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
          <Truck className="w-4 h-4" />
          <div className="text-sm font-bold">Ostatnie trasy w tym miesiącu</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 ml-auto">
            {stats?.recent?.length || 0} ostatnich
          </div>
        </div>
        <div>
          {(stats?.recent || []).length === 0 && (
            <div className="p-6 text-sm text-zinc-400">
              Brak tras w tym miesiącu. Sprawdź zakładkę „Doręczenia&rdquo; po nowe przypisania.
            </div>
          )}
          {(stats?.recent || []).map((r) => {
            const rate = r.total ? Math.round((r.delivered / r.total) * 100) : 0;
            return (
              <div key={r.id}
                   onClick={() => nav("/deliveries")}
                   className="px-4 py-3 border-b border-white/5 last:border-0 flex items-center gap-4 hover:bg-white/5 cursor-pointer"
                   data-testid={`driver-recent-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{r.name || "Bez nazwy"}</div>
                  <div className="text-[10px] font-mono text-white/50 flex items-center gap-2">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("pl-PL") : ""}
                    {r.vehicle_plate && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-white/80">
                        <Car className="w-3 h-3" /> {r.vehicle_plate}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-white/70">
                  {r.delivered}/{r.total}
                </div>
                <div className="text-xs font-bold w-14 text-right">
                  {rate}%
                </div>
                <ChevronRight className="w-4 h-4 text-white/40" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone = "zinc" }) {
  const bg = {
    emerald: "bg-emerald-500/25 ring-emerald-300/40 text-emerald-100",
    amber:   "bg-amber-500/25 ring-amber-300/40 text-amber-100",
    red:     "bg-red-500/25 ring-red-300/40 text-red-100",
    zinc:    "bg-white/10 ring-white/20 text-white/90",
  }[tone] || "bg-white/10 ring-white/20 text-white/90";
  return (
    <div className={`rounded-xl px-3 py-2 ring-1 ${bg}`}>
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-80">{label}</div>
      <div className="font-heading font-black text-2xl mt-0.5 text-white">{value}</div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, tone = "zinc", big = false, testId }) {  const toneMap = {
    emerald: "from-emerald-500 to-emerald-700",
    red:     "from-red-500 to-red-700",
    sky:     "from-sky-500 to-blue-700",
    amber:   "from-amber-500 to-orange-600",
    indigo:  "from-indigo-500 to-purple-700",
    zinc:    "from-zinc-600 to-zinc-800",
  };
  const gradient = toneMap[tone] || toneMap.zinc;
  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} text-white p-4 min-h-[110px] ring-1 ring-white/10 shadow-[0_10px_25px_-10px_rgba(0,0,0,0.6)]`}
    >
      <Icon className="absolute -right-4 -bottom-4 w-24 h-24 opacity-[0.10]" strokeWidth={1.5} />
      <div className="relative flex items-center gap-2">
        <div className="w-6 h-6 rounded-md bg-white/15 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
          {label}
        </div>
      </div>
      <div className={`relative mt-2 font-heading font-black tracking-tight ${big ? "text-4xl" : "text-3xl"}`}>
        {value}
      </div>
      {hint && (
        <div className="relative mt-1 text-[10px] text-white/70 font-mono truncate">
          {hint}
        </div>
      )}
    </div>
  );
}

function DriverHeroCard({ user, rpg, wallet }) {
  const nav = useNavigate();
  const level = rpg?.level ?? 1;
  const levelName = rpg?.level_name || "Rekrut";
  const nextName = rpg?.next_level_name;
  const xp = Number(rpg?.xp || 0);
  const cur = Number(rpg?.current_threshold || 0);
  const next = rpg?.next_threshold;
  const pct = Math.round((rpg?.progress || 0) * 100);
  const diamonds = Number(rpg?.diamonds || 0);
  const walletBalance = Number(wallet?.available_balance || 0);
  const approvedTotal = Number(wallet?.approved_total || 0);
  const boost = rpg?.active_boost;
  const displayName = user?.name || user?.username || user?.email?.split("@")[0] || "Kurier";
  const initials = displayName
    .split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "K";

  // Deterministic accent color per user for the avatar
  const accentPalette = [
    "from-red-500 via-orange-600 to-yellow-500",
    "from-purple-500 via-fuchsia-600 to-pink-500",
    "from-cyan-500 via-blue-600 to-indigo-600",
    "from-emerald-500 via-teal-600 to-cyan-600",
    "from-amber-500 via-orange-600 to-red-600",
    "from-fuchsia-500 via-purple-600 to-indigo-700",
  ];
  const hash = displayName.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const avatarBg = accentPalette[hash % accentPalette.length];

  const rankBadgeColor =
    level >= 11 ? "from-yellow-300 via-amber-400 to-orange-500"
    : level >= 8 ? "from-fuchsia-400 via-purple-500 to-indigo-600"
    : level >= 5 ? "from-cyan-400 via-blue-500 to-indigo-600"
    : "from-zinc-300 via-zinc-500 to-zinc-700";

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-950 via-purple-950 to-zinc-950 text-white p-5 md:p-7 ring-1 ring-white/10 shadow-[0_30px_80px_-25px_rgba(88,28,135,0.7)]"
         data-testid="driver-hero-card">
      {/* Animated background sparkles / grain */}
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 25% 15%, rgba(168,85,247,0.4) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(59,130,246,0.35) 0%, transparent 40%)" }} />
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
           style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />

      {/* ACTIVE BOOST BANNER */}
      {boost && (
        <div className="relative mb-4 rounded-xl bg-gradient-to-r from-orange-500 via-red-600 to-fuchsia-600 p-3 flex items-center gap-3 ring-1 ring-white/20 shadow-lg"
             data-testid="driver-active-boost">
          <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center animate-pulse">
            <Trophy className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
              // XP Boost aktywny
            </div>
            <div className="font-heading font-black text-lg uppercase tracking-tight truncate">
              {boost.name} <span className="text-amber-200">×{boost.multiplier}</span>
            </div>
          </div>
          <div className="text-right text-[10px] font-mono opacity-90 hidden sm:block">
            do {new Date(boost.ends_at).toLocaleString("pl-PL", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      )}

      <div className="relative flex flex-col md:flex-row items-stretch gap-5">
        {/* CHARACTER PORTRAIT */}
        <div className="flex items-center gap-4 md:flex-col md:items-center md:min-w-[220px]">
          <div className="relative">
            {/* Avatar */}
            <div className={`w-28 h-28 md:w-36 md:h-36 rounded-3xl bg-gradient-to-br ${avatarBg} ring-4 ring-white/20 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.8)] flex items-center justify-center relative overflow-hidden`}>
              <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-white/20" />
              <span className="relative text-4xl md:text-5xl font-heading font-black text-white drop-shadow-lg">
                {initials}
              </span>
            </div>
            {/* Level badge */}
            <div className={`absolute -bottom-3 -right-3 w-14 h-14 rounded-full bg-gradient-to-br ${rankBadgeColor} ring-4 ring-zinc-950 flex flex-col items-center justify-center shadow-xl`}
                 data-testid="driver-level-badge">
              <div className="text-[7px] font-mono uppercase tracking-widest text-white/70 leading-none">LVL</div>
              <div className="text-xl font-heading font-black text-white leading-none mt-0.5">{level}</div>
            </div>
          </div>
          <div className="md:text-center flex-1 md:flex-none">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">
              {displayName}
            </div>
            <div className="font-heading font-black text-xl md:text-2xl tracking-tight uppercase text-white leading-tight mt-0.5">
              {levelName}
            </div>
            {nextName && (
              <div className="text-[10px] font-mono text-fuchsia-200/80 mt-1">
                → {nextName}
              </div>
            )}
          </div>
        </div>

        {/* STATS PANEL */}
        <div className="flex-1 space-y-3">
          {/* XP BAR — the main progress indicator */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
                XP · Progres do następnego poziomu
              </div>
              <div className="font-mono text-xs text-white/90">
                {xp.toLocaleString("pl-PL")}{next != null && <span className="text-white/50"> / {next.toLocaleString("pl-PL")}</span>}
              </div>
            </div>
            <div className="relative h-4 rounded-full bg-black/50 overflow-hidden ring-1 ring-white/10">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-fuchsia-500 via-purple-400 to-cyan-300 shadow-[0_0_20px_rgba(168,85,247,0.6)] transition-all duration-1000"
                   style={{ width: `${pct}%` }} />
              <div className="absolute inset-0 flex items-center justify-center text-[10px] font-mono font-bold text-white mix-blend-difference">
                {pct}%
              </div>
            </div>
            {next != null && (
              <div className="text-[10px] font-mono text-white/50 mt-1">
                do awansu brakuje {Math.max(next - xp, 0).toLocaleString("pl-PL")} XP
              </div>
            )}
          </div>

          {/* STATS GRID: Diamenty · Portfel · Doręczone */}
          <div className="grid grid-cols-3 gap-2">
            <HeroStat
              label="Diamenty"
              value={diamonds.toLocaleString("pl-PL")}
              icon={Gem}
              tone="cyan"
              onClick={() => nav("/sklep")}
              testId="hero-diamonds"
            />
            <HeroStat
              label="Portfel"
              value={`${walletBalance.toLocaleString("pl-PL", { maximumFractionDigits: 2 })} zł`}
              icon={Wallet}
              tone="emerald"
              testId="hero-wallet"
            />
            <HeroStat
              label="Paczek"
              value={approvedTotal.toLocaleString("pl-PL")}
              icon={PackageCheck}
              tone="amber"
              testId="hero-packages"
            />
          </div>

          {/* Quick action buttons */}
          <div className="flex gap-2 flex-wrap pt-1">
            <HeroBtn onClick={() => nav("/osiagniecia")} testId="hero-btn-achievements" icon={Trophy}>
              Osiągnięcia ({rpg?.achievements_unlocked || 0}/{rpg?.achievements_total || 0})
            </HeroBtn>
            <HeroBtn onClick={() => nav("/sklep")} testId="hero-btn-shop" icon={ShoppingBag}>
              Sklep
            </HeroBtn>
            <HeroBtn onClick={() => nav("/zlecenia?view=swipe")} testId="hero-btn-route" icon={Truck}>
              Moja trasa
            </HeroBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroStat({ label, value, icon: Icon, tone = "cyan", onClick, testId }) {
  const tones = {
    cyan:    "from-cyan-500/30 to-blue-600/30 ring-cyan-300/40 text-cyan-100",
    emerald: "from-emerald-500/30 to-teal-600/30 ring-emerald-300/40 text-emerald-100",
    amber:   "from-amber-500/30 to-orange-600/30 ring-amber-300/40 text-amber-100",
  };
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      data-testid={testId}
      className={`relative rounded-2xl bg-gradient-to-br ${tones[tone]} ring-1 p-3 text-left transition-transform ${onClick ? "hover:scale-[1.02] cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 opacity-80" />
        <span className="text-[9px] font-mono uppercase tracking-widest opacity-80">{label}</span>
      </div>
      <div className="font-heading font-black text-xl md:text-2xl mt-1 text-white">
        {value}
      </div>
    </button>
  );
}

function HeroBtn({ onClick, icon: Icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/25 ring-1 ring-white/15 text-xs font-bold uppercase tracking-widest text-white transition-colors backdrop-blur-sm"
    >
      <Icon className="w-3.5 h-3.5" />
      {children}
    </button>
  );
}

function RpgWidget({ rpg }) {
  const nav = useNavigate();
  const newly = rpg?.newly_unlocked || [];
  useEffect(() => {
    // Fire a celebratory toast once per fresh unlock, per session load.
    newly.forEach((a) => {
      toast.success(`🏆 Nowe osiągnięcie: ${a.name}! +${a.xp_reward || 0} XP, +${a.diamond_reward || 0}💎`);
    });
  }, [newly.map((a) => a.code).join("|")]);
  if (!rpg) return null;
  const pct = Math.round((rpg.progress || 0) * 100);
  const nextGap = rpg.next_threshold != null ? Math.max(rpg.next_threshold - rpg.xp, 0) : 0;
  return (
    <div
      data-testid="driver-rpg"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-fuchsia-700 to-zinc-950 text-white p-5 md:p-6 ring-1 ring-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)]"
    >
      <Trophy className="absolute -right-8 -bottom-10 w-56 h-56 opacity-[0.10]" strokeWidth={1.5} />
      <div className="relative flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center ring-1 ring-white/25">
          <Trophy className="w-4 h-4" />
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
          // Poziom kuriera · System RPG
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => nav("/osiagniecia")}
            data-testid="btn-rpg-achievements"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/15 hover:bg-white/25 ring-1 ring-white/20 text-xs font-bold uppercase tracking-widest transition-colors"
          >
            <Star className="w-3.5 h-3.5" /> Osiągnięcia
          </button>
          <button
            onClick={() => nav("/sklep")}
            data-testid="btn-rpg-shop"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-cyan-400/25 hover:bg-cyan-400/45 ring-1 ring-cyan-200/50 text-xs font-bold uppercase tracking-widest text-cyan-50 transition-colors"
          >
            <ShoppingBag className="w-3.5 h-3.5" /> Sklep
          </button>
        </div>
      </div>

      <div className="relative mt-3 flex items-end flex-wrap gap-4 md:gap-8">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-white/60">
            LVL {rpg.level} / 13
          </div>
          <div className="text-3xl md:text-4xl font-heading font-black tracking-tight uppercase">
            {rpg.level_name}
          </div>
          <div className="text-xs mt-1 text-white/80">
            {rpg.xp?.toLocaleString("pl-PL")} XP{" "}
            {rpg.next_threshold != null && (
              <span className="text-white/60">
                / do &bdquo;<span className="text-fuchsia-200 font-bold">{rpg.next_level_name}</span>&rdquo;
                brakuje {nextGap.toLocaleString("pl-PL")} XP
              </span>
            )}
          </div>
        </div>

        {/* Diamonds pill */}
        <div
          data-testid="driver-diamonds"
          onClick={() => nav("/sklep")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-cyan-500/25 ring-1 ring-cyan-200/50 cursor-pointer hover:bg-cyan-400/40 transition-colors"
        >
          <Gem className="w-6 h-6 text-cyan-100" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">Diamenty</div>
            <div className="text-2xl font-heading font-black text-cyan-50">
              {(rpg.diamonds ?? 0).toLocaleString("pl-PL")}
            </div>
          </div>
        </div>

        <div className="text-right ml-auto">
          <div className="text-3xl font-heading font-black">
            {rpg.achievements_unlocked}/{rpg.achievements_total}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
            osiągnięć
          </div>
        </div>
      </div>

      {/* progress bar */}
      <div className="relative mt-4 h-2.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-fuchsia-400 via-purple-300 to-white"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="relative mt-1 text-[10px] font-mono text-white/60">
        Postęp: {pct}%
      </div>

      {/* achievements chips */}
      <div className="relative mt-4 flex gap-2 flex-wrap">
        {(rpg.achievements || []).map((a) => (
          <div
            key={a.id}
            title={a.desc}
            data-testid={`achievement-${a.id}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest ring-1 ${
              a.unlocked
                ? "bg-amber-500/25 text-amber-100 ring-amber-400/50"
                : "bg-white/5 text-white/40 ring-white/10"
            }`}
          >
            {a.unlocked ? <Star className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
            {a.name}
          </div>
        ))}
      </div>
    </div>
  );
}

